import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ConfigProvider, App as AntApp } from "antd";
import "antd/dist/reset.css";
import Layout from "./components/layout";
import LoginPage from "./pages/Login";
import SettingsPage from "./pages/Settings/SettingsPage";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import RoleBasedRoute from "./components/auth/RoleBasedRoute";
import AuthErrorHandler from "./components/auth/AuthErrorHandler";
import RecordExpense from "./pages/Expenses/RecordExpense";
import ManageExpense from "./pages/Expenses/ManageExpense";
import ExpenseOverview from "./pages/Expenses/Overview";
import SalesPage from "./pages/Sales/Overview";
import Reports from "./pages/Reports";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./api/queryClient";
import { useAuth } from "./context/AuthContext";
import UserPage from "./pages/Users";
import ExpenseGroups from "./pages/Reports/components/ExpenseGroups";
import ExpenseGroupDetail from "./pages/Reports/components/ExpenseGroups/components/ExpenseGroupDetail";

// Role-based redirect component
const RoleBasedRedirect = () => {
  const { user } = useAuth();

  if (user?.roles.includes("worker")) {
    return <Navigate to="/expenses/record" replace />;
  } else if (
    user?.roles.includes("admin") ||
    user?.roles.includes("superuser")
  ) {
    return <Navigate to="/overview" replace />;
  } else {
    // Default fallback for users with other roles
    return <Navigate to="/expenses/record" replace />;
  }
};

function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#1890ff",
        },
      }}
      wave={{ disabled: true }}
    >
      <QueryClientProvider client={queryClient}>
        <AntApp>
          <AuthProvider>
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              {/* Global authentication error handler */}
              <AuthErrorHandler />

              <Routes>
                <Route path="/login" element={<LoginPage />} />

                {/* Routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<RoleBasedRedirect />} />

                  {/* Admin-only routes */}
                  <Route
                    path="overview"
                    element={
                      <RoleBasedRoute allowedRoles={["admin", "superuser"]}>
                        <Reports />
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="overview/expense-groups"
                    element={
                      <RoleBasedRoute allowedRoles={["admin", "superuser"]}>
                        <ExpenseGroups />
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="overview/expense-groups/detail"
                    element={
                      <RoleBasedRoute allowedRoles={["admin", "superuser"]}>
                        <ExpenseGroupDetail />
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="settings"
                    element={
                      <RoleBasedRoute allowedRoles={["admin", "superuser"]}>
                        <SettingsPage />
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="users"
                    element={
                      <RoleBasedRoute allowedRoles={["admin", "superuser"]}>
                        <UserPage />
                      </RoleBasedRoute>
                    }
                  />
                  <Route
                    path="sales"
                    element={
                      <RoleBasedRoute allowedRoles={["admin", "superuser"]}>
                        <SalesPage />
                      </RoleBasedRoute>
                    }
                  />

                  {/* Shared routes */}
                  <Route path="expenses">
                    <Route
                      index
                      element={<Navigate to="/expenses/details" replace />}
                    />
                    <Route path="record" element={<RecordExpense />} />
                    <Route
                      path="manage"
                      element={
                        <RoleBasedRoute allowedRoles={["admin", "superuser"]}>
                          <ManageExpense />
                        </RoleBasedRoute>
                      }
                    />
                    <Route path="details" element={<ExpenseOverview />} />
                  </Route>
                </Route>
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </AntApp>
      </QueryClientProvider>
    </ConfigProvider>
  );
}

export default App;
